// ------------------------------
// src/pages/BlogDetails.js
// ------------------------------

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useParams,
  Link,
} from "react-router-dom";

import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";

import DOMPurify from "dompurify";

import { db } from "../firebaseConfig";
import Header from "../components/Header";
import EarnWithUsModal from "../components/EarnWithUsModal";
import BlogSEO from "../components/BlogSEO";


import "./blog.css";

export function BlogDetails() {

  const { slug } = useParams();

  const [post, setPost] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
const [isMobileMenuOpen, setIsMobileMenuOpen] =
    useState(false);

  const [scrolled, setScrolled] = useState(false);

  const [showEarnModal, setShowEarnModal] =
    useState(false);

  const theme = "light";

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // FETCH ARTICLE

  useEffect(() => {

    const fetchPost = async () => {

      try {

        setLoading(true);

        setError("");

        const q = query(
          collection(db, "blogs"),
          where("slug", "==", slug),
          where("status", "==", "published"),
          limit(1)
        );

        const snap = await getDocs(q);

        if (snap.empty) {

          setError("Post not found.");

          setPost(null);

        } else {

          setPost({
            id: snap.docs[0].id,
            ...snap.docs[0].data(),
          });

        }

      } catch (e) {

        console.error(e);

        setError("Couldn't load this article.");

      } finally {

        setLoading(false);

      }

    };

    fetchPost();

  }, [slug]);

  // SAFE VALUES

  const safeContent = post?.content || "";

  // READING TIME

  const readingTime = useMemo(() => {

    const words = safeContent
      .replace(/<[^>]+>/g, "")
      .trim()
      .split(/\s+/).length;

    return `${Math.max(
      1,
      Math.ceil(words / 200)
    )} min read`;

  }, [safeContent]);

  // SANITIZED CONTENT

  const cleanHTML = useMemo(() => {

    return DOMPurify.sanitize(safeContent);

  }, [safeContent]);

  const publishDate =
    post?.createdAt?.toDate?.().toLocaleDateString(
      "en-IN",
      {
        day: "numeric",
        month: "long",
        year: "numeric",
      }
    ) || "";

  // LOADING
  if (loading) {
    return (
      <section className="blox-pad">
        <BlogSEO loading={true} />
        <div className="blox-shell">
          <div className="blox-articleLoading">
            Loading article...
          </div>
        </div>
      </section>
    );
  }

  // ERROR or NOT FOUND
  if (error || !post) {
    return (
      <section className="blox-pad">
        <BlogSEO error={error || "Post not found."} slug={slug} />
        <div className="blox-shell">
          <div className="blox-errorCard">
            <h2>Oops</h2>
            <p>{error || "Post not found."}</p>
            <Link
              to="/blog"
              className="blox-btn"
            >
              Back to Blog
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <article className="home-page blox-pad light-theme">
      <EarnWithUsModal
        showEarnModal={showEarnModal}
        setShowEarnModal={setShowEarnModal}
        theme={theme}
      />

      <Header
        scrolled={scrolled}
        isMobileMenuOpen={isMobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        setShowEarnModal={setShowEarnModal}
      />

      {/* Dynamic SEO metadata & Schema.org JSON-LD */}
      <BlogSEO post={post} slug={slug} />

      {/* HERO */}

      {post.coverImage && (
        <div className="blox-articleHero">
          <img
            className="blox-heroImg"
            src={post.coverImage}
            alt={`${post.title} - DOR Dress On Rent`}
            onError={(e) => {
              e.target.src =
                "https://placehold.co/1600x900/F3EFEA/111?text=DOR+Blog";
            }}
          />
          <div className="blox-heroOverlay" />
        </div>
      )}

      <div className="blox-shell">
        {/* HEADER */}
        <header className="blox-articleHead">
          {/* Breadcrumb Navigation */}
          <nav aria-label="Breadcrumb" className="blox-breadcrumb">
            <Link to="/" className="blox-breadcrumb-link">Home</Link>
            <span className="blox-breadcrumb-sep">/</span>
            <Link to="/blog" className="blox-breadcrumb-link">Blog</Link>
            <span className="blox-breadcrumb-sep">/</span>
            <span className="blox-breadcrumb-current" aria-current="page">{post.title}</span>
          </nav>

          <div className="blox-articleMeta">
            <span>{readingTime}</span>
            <span>•</span>
            <span>{publishDate}</span>
          </div>

          <h1 className="blox-title">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="blox-articleExcerpt">
              {post.excerpt}
            </p>
          )}

          {/* AUTHOR */}
          <div className="blox-authorRow">
            <div className="blox-authorAvatar">
              {(post.author || "D")
                .charAt(0)
                .toUpperCase()}
            </div>
            <div>
              <p className="blox-authorName">
                {post.author || "DOR"}
              </p>
              <p className="blox-authorRole">
                Fashion Editorial
              </p>
            </div>
          </div>

          {/* TAGS */}
          {Array.isArray(post.tags) &&
            post.tags.length > 0 && (
              <div className="blox-tags">
                {post.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/blog?tag=${encodeURIComponent(t)}`}
                    className="blox-chip"
                  >
                    #{t}
                  </Link>
                ))}
              </div>
            )}
        </header>

        {/* CONTENT */}
        <div className="blox-article">
          <div
            className="blox-articleContent"
            dangerouslySetInnerHTML={{
              __html: cleanHTML,
            }}
          />

          {/* Internal Linking & Next Reads */}
          <div className="blox-explore-box">
            <h3 className="blox-explore-title">Explore Outfits on Rent with DOR</h3>
            <p className="blox-explore-sub">
              Looking for stunning designer wear for weddings, receptions, or parties in Pune and Nagpur? Browse our collections or read more fashion advice.
            </p>
            <div className="blox-explore-links">
              <Link to="/collection/women/Lehenga" className="blox-explore-link">
                Women's Lehengas on Rent →
              </Link>
              <Link to="/collection/men/Sherwani" className="blox-explore-link">
                Men's Sherwanis on Rent →
              </Link>
              <Link to="/blog" className="blox-explore-link">
                More Style Guides →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}